import React from "react";
import PropTypes from "prop-types";

/**
 * TabBar — Figma's "Tabs / Group": a strip of buttons, one per tab, each labelled with its count
 * ("Experts (10)"), or just its name when it is empty. No reusable tabs primitive exists in the app,
 * so this is a local, presentational control: it renders the tabs it's given and calls
 * `onSelect(key)`; it holds no state of its own.
 *
 * @param {Object} props
 * @param {Array} props.tabs - `{ key, label, rows }[]` — the count is `rows.length` (deduped count)
 * @param {string} props.activeTabKey - Key of the currently active tab
 * @param {Function} props.onSelect - `(key) => void` invoked when a tab is clicked
 */
function TabBar({ tabs, activeTabKey, onSelect }) {
  return (
    <ul className="represents-tabs">
      {tabs.map((tab) => {
        const count = (tab.rows || []).length;

        return (
          <li key={tab.key}>
            <button
              type="button"
              className={`represents-tab ${tab.key === activeTabKey ? "active" : ""}`.trim()}
              aria-current={tab.key === activeTabKey ? "page" : undefined}
              onClick={() => onSelect(tab.key)}
            >
              {tab.label}
              {count > 0 && <span className="represents-tab-count"> ({count})</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

TabBar.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      rows: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })),
    }),
  ).isRequired,
  activeTabKey: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default TabBar;
